import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { useInnerHeroImage } from '../hooks/useInnerHeroImage';
import {
  INNER_HERO_SUBTITLE_CLASS,
  INNER_HERO_TITLE_CLASS,
  InnerHeroBackground,
  InnerHeroShell,
} from '../ui/InnerHeroShell';

/**
 * Virtual Tour page hero. Keeps the `virtual-tour-hero.*` keys the shared
 * PageHero instance saved to, so existing edits carry over.
 */

const TITLE = '<p>Virtual Tour</p>';
const SUBTITLE = '<p>Walk Through Apartment Homes From Anywhere</p>';

const bgImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'property_featured_image' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Virtual Tour banner' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'An attractive apartment interior photo representative of the spaces shown in the virtual tours — a living area, kitchen, or bedroom. Prefer staged or furnished interiors when the property has them. Alt text describes the chosen photo.',
    context:
      'Full-bleed inner page hero banner behind the "Virtual Tour" title, under a dark overlay; crops to a wide landscape strip. Must be sharp, well lit and uncluttered — never a floor plan drawing, map, logo, or graphic. Use a photo not already used by the homepage hero or the other inner page heroes (Floor Plans, Amenities, Gallery, Neighborhood, Contact); Floor Plans also shows an interior, so pick a different room or shot. Reuse a photo only if no unused interior fits.',
  },
};

export function VirtualTourHero() {
  const bg = useInnerHeroImage('virtual-tour-hero.bg_image', bgImageDef, 'virtual-tour', {
    altText: 'Virtual Tour banner',
  });

  return (
    <InnerHeroShell
      background={
        <EditableSlotGroup
          groupPath="virtual-tour-hero.bg_image"
          definition={bgImageDef}
          className="absolute inset-0"
        >
          <InnerHeroBackground image={bg.image} alt={bg.alt} />
        </EditableSlotGroup>
      }
    >
      <RichTextContent
        path="virtual-tour-hero.subtitle"
        label="Subtitle"
        defaultValue={SUBTITLE}
        as="h2"
        className={INNER_HERO_SUBTITLE_CLASS}
      />
      <RichTextContent
        path="virtual-tour-hero.title"
        label="Title"
        defaultValue={TITLE}
        as="h1"
        className={INNER_HERO_TITLE_CLASS}
      />
    </InnerHeroShell>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'VirtualTourHero',
  displayName: 'Virtual Tour Hero',
  tokens: {
    'virtual-tour-hero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>Walk Through Apartment Homes From Anywhere</p>',
    },
    'virtual-tour-hero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Virtual Tour</p>',
    },
  },
  slotGroups: {
    'virtual-tour-hero.bg_image': bgImageDef,
  },
};

(VirtualTourHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default VirtualTourHero;
